/**
 * Route guard for the call-center CRM workspace
 * (CALL-CENTER-CRM-WORKSPACE-V1). Mirrors RequireLeadCockpit: the pure
 * decision lives in decideCallCenterWorkspaceAccess
 * (callcenter-workspace-view.ts) and keys off the SERVER-computed
 * /me callCenterWorkspaceEnabled signal (per-org callcenter.workspace_enabled
 * config x the workspace audience). Gate off OR unauthorized role = redirect
 * to the caller's own homePath (invisible, never a dead door -- LES-016).
 *
 * Backend route auth (getCallcenterWorkspace re-checks gate + audience +
 * scope) remains the real security boundary; this guard is UX cleanliness.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideCallCenterWorkspaceAccess } from '@/dashboard/callcenter/callcenter-workspace-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireCallCenterWorkspaceViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireCallCenterWorkspaceView({
  loading,
  error,
  me,
  children,
}: RequireCallCenterWorkspaceViewProps): JSX.Element | null {
  const decision = decideCallCenterWorkspaceAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireCallCenterWorkspace(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireCallCenterWorkspaceView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
